import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  useTheme,
  InputAdornment,
  alpha,
  styled,
} from '@mui/material';
import { Environment } from '../../environment';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';

interface IFerramentasDaListagemProps {
  textoTitulo?: string; 
  mostrarTextoTitulo?: boolean;
  textoDaBusca?: string;
  mostrarInputBusca?: boolean;
  aoMudarTextoDeBusca?: (novoTexto: string) => void;
  textoBotaoNovo?: string;
  mostrarBotaoNovo?: boolean;
  aoClicarEmNovo?: () => void;
}

// Componente estilizado para o TextField
const StyledTextField = styled(TextField)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(1),
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: alpha(theme.palette.primary.main, 0.3),
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
    borderWidth: 1,
  },
}));

export const FerramentasDaListagem: React.FC<IFerramentasDaListagemProps> = ({
  textoDaBusca = '',
  textoTitulo = '',
  mostrarTextoTitulo = false,
  aoMudarTextoDeBusca,
  mostrarInputBusca = false,
  aoClicarEmNovo,
  textoBotaoNovo = 'Novo',
  mostrarBotaoNovo = true,
}) => {
  const theme = useTheme();

  return (
    <Box
      gap={2}
      marginX={2}
      marginBottom={2}
      padding={2}
      display="flex"
      alignItems="center"
      borderRadius={2}
      boxShadow={1}
      sx={{
        backgroundColor: 'background.paper',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      }}
    >
      {/* Título */}
      {mostrarTextoTitulo && (
        <Box sx={{ minWidth: 200, flexShrink: 0 }}>
          <Typography
            variant="h6"
            component="h2"
            color="primary.main"
            fontWeight="medium"
            sx={{
              fontFamily: theme.typography.fontFamily,
            }}
          >
            {textoTitulo}
          </Typography>
        </Box>
      )}
      
      {/* Campo de busca */}
      {mostrarInputBusca && (
        <Box sx={{ 
          minWidth: 250, 
          maxWidth: 400, 
          flexGrow: 1,
          marginRight: 'auto' // Empurra o botão para a direita
        }}>
          <StyledTextField
            fullWidth
            size="small"
            value={textoDaBusca}
            placeholder={Environment.INPUT_DE_BUSCA}
            onChange={(e) => aoMudarTextoDeBusca?.(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      )}

      {/* Espaço flexível que empurra o botão para a direita */}
      {mostrarInputBusca && <Box sx={{ flexGrow: 1 }} />}

      {/* Botão Novo - sempre alinhado à direita */}
      <Box sx={{ flexShrink: 0 }}>
        {mostrarBotaoNovo && (
          <Button
            color="primary"
            disableElevation
            variant="contained"
            onClick={aoClicarEmNovo}
            startIcon={<AddIcon />}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 'medium',
              px: 3,
              py: 1,
              boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.2)}`,
              '&:hover': {
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
              }
            }}
          >
            {textoBotaoNovo}
          </Button>
        )}
      </Box>
    </Box>
  );
};